import React, { useState } from 'react';
import { Box, Typography, Button, RadioGroup, FormControlLabel, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Switch, Radio } from '@mui/material';
import SaveConfirmModal from './SaveConfirmModal';

// iOS 스타일 토글 스위치 (예약 불가능 상태용 - 색상 변경)
const IOSSwitch = styled(props => <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />)(
  ({ theme }) => ({
    width: 40,
    height: 20,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(20px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#58B9C1',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 16,
      height: 16,
    },
    '& .MuiSwitch-track': {
      borderRadius: 10,
      backgroundColor: '#D9D9D9',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }),
);

// 커스텀 라디오 버튼 스타일 (예약 불가능 상태용)
const CustomRadio = styled(Radio)(({ disabled }) => ({
  padding: 0,
  '&.MuiRadio-root': {
    color: '#D9D9D9',
  },
  '&.Mui-checked': {
    color: disabled ? '#A6A6A6' : '#58B9C1',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 24,
  },
}));

// 설정 행 컴포넌트
const SettingRow = ({ label, children, labelWidth = '160px', disabled = false }) => (
  <Box sx={{ display: 'flex', gap: '40px', alignItems: 'flex-start' }}>
    <Typography
      sx={{
        width: labelWidth,
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        fontSize: '16px',
        fontWeight: 500,
        color: disabled ? '#A6A6A6' : '#262626',
      }}>
      {label}
    </Typography>
    <Box sx={{ width: '462px' }}>{children}</Box>
  </Box>
);

const ReservationDisabledPanel = ({ disabledSettings, setDisabledSettings, onSave }) => {
  const [saveConfirmModalOpen, setSaveConfirmModalOpen] = useState(false);

  // 기본 설정 상태 초기화 (DB 컬럼명: SPOT_STATUS_SHOW_YN, SHORT_TIME_STATUS_SHOW_YN)
  const settings = disabledSettings || {
    spotStatusShowYn: 'Y',
    shortTimeStatusShowYn: 'Y',
  };

  // 토글용 boolean 변환
  const isSpotStatusShow = settings.spotStatusShowYn === 'Y';

  const handleSettingsChange = (key, value) => {
    setDisabledSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <>
      {/* 예약 불가능 설정 카드 */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #D9D9D9',
          borderRadius: '8px',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          width: '100%',
        }}>
        {/* 타이틀 */}
        <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#3E9FA7' }}>회원앱에서 예약 불가능</Typography>

        {/* 타석 현황 회원앱 노출 섹션 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: isSpotStatusShow ? '#58B9C1' : '#A6A6A6',
              }}>
              타석 현황 회원앱 노출
            </Typography>
            <IOSSwitch
              checked={isSpotStatusShow}
              onChange={e => handleSettingsChange('spotStatusShowYn', e.target.checked ? 'Y' : 'N')}
            />
          </Box>

          {/* 숏타임 현황 노출여부 */}
          <Box
            sx={{
              opacity: isSpotStatusShow ? 1 : 0.5,
              pointerEvents: isSpotStatusShow ? 'auto' : 'none',
            }}>
            <SettingRow label="숏타임 현황 노출여부" disabled={!isSpotStatusShow}>
              <RadioGroup
                value={settings.shortTimeStatusShowYn}
                onChange={e => {
                  if (!isSpotStatusShow) return;
                  handleSettingsChange('shortTimeStatusShowYn', e.target.value);
                }}>
                <FormControlLabel
                  value="Y"
                  control={<CustomRadio disabled={!isSpotStatusShow} />}
                  label="노출"
                  sx={{
                    height: '40px',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '16px',
                      fontWeight: 500,
                      color: isSpotStatusShow ? '#262626' : '#A6A6A6',
                      marginLeft: '8px',
                    },
                  }}
                />

                <FormControlLabel
                  value="N"
                  control={<CustomRadio disabled={!isSpotStatusShow} />}
                  label="미노출"
                  sx={{
                    height: '40px',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '16px',
                      fontWeight: 500,
                      color: isSpotStatusShow ? '#262626' : '#A6A6A6',
                      marginLeft: '8px',
                    },
                  }}
                />
              </RadioGroup>
            </SettingRow>
          </Box>
        </Box>

        <Divider />
      </Box>

      {/* 설정 저장 버튼 */}
      <Button
        variant="contained"
        onClick={() => setSaveConfirmModalOpen(true)}
        sx={{
          width: '240px',
          height: '48px',
          backgroundColor: '#3E9FA7',
          borderRadius: '8px',
          fontSize: '20px',
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#358A91',
          },
        }}>
        설정저장
      </Button>

      {/* 설정 저장 확인 모달 */}
      <SaveConfirmModal
        open={saveConfirmModalOpen}
        onClose={() => setSaveConfirmModalOpen(false)}
        onConfirm={() => {
          setSaveConfirmModalOpen(false);
          onSave();
        }}
      />
    </>
  );
};

export default ReservationDisabledPanel;
